/**
 * Input schemas for the `admin.*` tRPC procedures.
 *
 * Admin mutations accept curator-authored content for outbreaks,
 * timeline events, news items, FAQs, advisories, vessels, and admin
 * users themselves. Per-locale text columns (`*_locale_{en|ar|fr}`)
 * are optional individually but the application layer validates that
 * Arabic content stays Khaleeji (no machine-translated MSA — see
 * CLAUDE.md i18n rule).
 *
 * Optimistic concurrency: every mutation that can race (`outbreaks`,
 * `timeline_events`, `faqs`) carries a `lastUpdatedAt` field. The
 * server compares against the row's current `updated_at`; mismatch =>
 * `CONFLICT` so the editor sees a merge prompt instead of silently
 * stomping a colleague's edit.
 */

import { z } from "zod";

import { advisorySeveritySchema } from "./advisories.js";
import { newsSeveritySchema } from "./news.js";
import {
  outbreakRiskLevelSchema,
  outbreakStatusSchema,
  outbreakSyndromeSchema,
} from "./outbreak.js";
import { timelineSeveritySchema } from "./timeline.js";
import { publicLocaleSchema } from "./locales.js";

// ---------------------------------------------------------------------------
// Shared sub-schemas
// ---------------------------------------------------------------------------

const localeSchema = publicLocaleSchema;
const slugSchema = z
  .string()
  .min(2)
  .max(100)
  .regex(/^[a-z0-9][a-z0-9-]*$/, {
    message: "Slug must be lowercase, alphanumeric, hyphen-separated",
  });
const isoCountrySchema = z
  .string()
  .length(2)
  .regex(/^[A-Z]{2}$/, { message: "Must be a 2-letter ISO 3166-1 alpha-2 code" });

// ---------------------------------------------------------------------------
// admin.outbreaks.*
// ---------------------------------------------------------------------------

export const adminOutbreakCreateInputSchema = z.object({
  slug: slugSchema,
  name: z.string().min(1).max(200),
  virusStrain: z.string().max(50).optional(),
  syndrome: outbreakSyndromeSchema.default("unknown"),
  status: outbreakStatusSchema.default("emerging"),
  riskLevelWho: outbreakRiskLevelSchema.default("low"),
  startedAt: z.coerce.date(),
  resolvedAt: z.coerce.date().nullable().optional(),
  summary: z.string().optional(),
  summaryLocaleEn: z.string().optional(),
  summaryLocaleAr: z.string().optional(),
  summaryLocaleFr: z.string().optional(),
  populationAtRisk: z.number().int().nonnegative().optional(),
  primaryCountry: isoCountrySchema.nullable().optional(),
});
export type AdminOutbreakCreateInput = z.infer<
  typeof adminOutbreakCreateInputSchema
>;

export const adminOutbreakUpdateInputSchema = z.object({
  id: z.number().int().positive(),
  /** Optimistic-concurrency token: client's last-known `updatedAt`. */
  lastUpdatedAt: z.coerce.date(),
  patch: z
    .object({
      slug: slugSchema.optional(),
      name: z.string().min(1).max(200).optional(),
      virusStrain: z.string().max(50).nullable().optional(),
      syndrome: outbreakSyndromeSchema.optional(),
      status: outbreakStatusSchema.optional(),
      riskLevelWho: outbreakRiskLevelSchema.optional(),
      startedAt: z.coerce.date().optional(),
      resolvedAt: z.coerce.date().nullable().optional(),
      summary: z.string().nullable().optional(),
      summaryLocaleEn: z.string().nullable().optional(),
      summaryLocaleAr: z.string().nullable().optional(),
      summaryLocaleFr: z.string().nullable().optional(),
      populationAtRisk: z.number().int().nonnegative().nullable().optional(),
      primaryCountry: isoCountrySchema.nullable().optional(),
    })
    .refine((p) => Object.keys(p).length > 0, {
      message: "patch must contain at least one field",
    }),
});
export type AdminOutbreakUpdateInput = z.infer<
  typeof adminOutbreakUpdateInputSchema
>;

export const adminOutbreakArchiveInputSchema = z.object({
  id: z.number().int().positive(),
  lastUpdatedAt: z.coerce.date(),
});

export const adminOutbreakSetPrimaryInputSchema = z.object({
  id: z.number().int().positive(),
});

// ---------------------------------------------------------------------------
// admin.timeline.*
// ---------------------------------------------------------------------------

export const adminTimelineCreateInputSchema = z.object({
  outbreakId: z.number().int().positive(),
  occurredAt: z.coerce.date(),
  title: z.string().min(1).max(300),
  titleLocaleEn: z.string().max(300).optional(),
  titleLocaleAr: z.string().max(300).optional(),
  titleLocaleFr: z.string().max(300).optional(),
  body: z.string().optional(),
  bodyLocaleEn: z.string().optional(),
  bodyLocaleAr: z.string().optional(),
  bodyLocaleFr: z.string().optional(),
  severity: timelineSeveritySchema.default("info"),
  tags: z.array(z.string().min(1).max(50)).optional(),
  newsItemId: z.number().int().positive().optional(),
});
export type AdminTimelineCreateInput = z.infer<
  typeof adminTimelineCreateInputSchema
>;

export const adminTimelineUpdateInputSchema = z.object({
  id: z.number().int().positive(),
  patch: z
    .object({
      occurredAt: z.coerce.date().optional(),
      title: z.string().min(1).max(300).optional(),
      titleLocaleEn: z.string().max(300).nullable().optional(),
      titleLocaleAr: z.string().max(300).nullable().optional(),
      titleLocaleFr: z.string().max(300).nullable().optional(),
      body: z.string().nullable().optional(),
      bodyLocaleEn: z.string().nullable().optional(),
      bodyLocaleAr: z.string().nullable().optional(),
      bodyLocaleFr: z.string().nullable().optional(),
      severity: timelineSeveritySchema.optional(),
      tags: z.array(z.string().min(1).max(50)).nullable().optional(),
    })
    .refine((p) => Object.keys(p).length > 0, {
      message: "patch must contain at least one field",
    }),
});

export const adminTimelineDeleteInputSchema = z.object({
  id: z.number().int().positive(),
});

// ---------------------------------------------------------------------------
// admin.news.*
// ---------------------------------------------------------------------------

export const adminNewsListInputSchema = z.object({
  curated: z.boolean().optional(),
  outbreakId: z.number().int().positive().nullable().optional(),
  cursor: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(100).default(50),
});

export const adminNewsCurateInputSchema = z.object({
  id: z.number().int().positive(),
  curated: z.boolean(),
  outbreakId: z.number().int().positive().nullable().optional(),
});

export const adminNewsEditHeadlineInputSchema = z.object({
  id: z.number().int().positive(),
  headline: z.string().min(1).max(500).optional(),
  headlineLocaleEn: z.string().max(500).nullable().optional(),
  headlineLocaleAr: z.string().max(500).nullable().optional(),
  headlineLocaleFr: z.string().max(500).nullable().optional(),
  severity: newsSeveritySchema.optional(),
});

export const adminNewsDeleteInputSchema = z.object({
  id: z.number().int().positive(),
});

// ---------------------------------------------------------------------------
// admin.faqs.*
// ---------------------------------------------------------------------------

export const adminFaqCreateInputSchema = z.object({
  outbreakId: z.number().int().positive().nullable().optional(),
  locale: localeSchema,
  question: z.string().min(1).max(500),
  answer: z.string().min(1),
  position: z.number().int().min(0).default(0),
  publishedAt: z.coerce.date().nullable().optional(),
});

export const adminFaqUpdateInputSchema = z.object({
  id: z.number().int().positive(),
  patch: z
    .object({
      question: z.string().min(1).max(500).optional(),
      answer: z.string().min(1).optional(),
      position: z.number().int().min(0).optional(),
      publishedAt: z.coerce.date().nullable().optional(),
      locale: localeSchema.optional(),
    })
    .refine((p) => Object.keys(p).length > 0, {
      message: "patch must contain at least one field",
    }),
});

export const adminFaqReorderInputSchema = z.object({
  outbreakId: z.number().int().positive().nullable().optional(),
  locale: localeSchema,
  /** Ordered list of FAQ ids; index becomes the new position. */
  orderedIds: z.array(z.number().int().positive()).min(1),
});

export const adminFaqDeleteInputSchema = z.object({
  id: z.number().int().positive(),
});

// ---------------------------------------------------------------------------
// admin.advisories.*
// ---------------------------------------------------------------------------

export const adminAdvisoryCreateInputSchema = z.object({
  outbreakId: z.number().int().positive(),
  locale: localeSchema,
  countryCode: isoCountrySchema.nullable().optional(),
  severity: advisorySeveritySchema.default("info"),
  headline: z.string().min(1).max(300),
  body: z.string().min(1),
  publishedAt: z.coerce.date().nullable().optional(),
  expiresAt: z.coerce.date().nullable().optional(),
});

export const adminAdvisoryUpdateInputSchema = z.object({
  id: z.number().int().positive(),
  patch: z
    .object({
      countryCode: isoCountrySchema.nullable().optional(),
      severity: advisorySeveritySchema.optional(),
      headline: z.string().min(1).max(300).optional(),
      body: z.string().min(1).optional(),
      publishedAt: z.coerce.date().nullable().optional(),
      expiresAt: z.coerce.date().nullable().optional(),
    })
    .refine((p) => Object.keys(p).length > 0, {
      message: "patch must contain at least one field",
    }),
});

export const adminAdvisoryDeleteInputSchema = z.object({
  id: z.number().int().positive(),
});

export const adminAdvisoryExpireInputSchema = z.object({
  id: z.number().int().positive(),
});

// ---------------------------------------------------------------------------
// admin.vessels.*
// ---------------------------------------------------------------------------

const mmsiSchema = z
  .string()
  .regex(/^\d{9}$/, { message: "MMSI must be 9 digits" });

export const adminVesselCreateInputSchema = z.object({
  mmsi: mmsiSchema,
  imo: z.string().regex(/^\d{7}$/, { message: "IMO must be 7 digits" }).optional(),
  name: z.string().min(1).max(200),
  flagCountryCode: isoCountrySchema.optional(),
  vesselType: z.string().max(50).optional(),
  operator: z.string().max(200).optional(),
  outbreakId: z.number().int().positive().nullable().optional(),
  trackingEnabled: z.boolean().default(true),
  notes: z.string().optional(),
});

export const adminVesselUpdateInputSchema = z.object({
  id: z.number().int().positive(),
  patch: z
    .object({
      imo: z
        .string()
        .regex(/^\d{7}$/, { message: "IMO must be 7 digits" })
        .nullable()
        .optional(),
      name: z.string().min(1).max(200).optional(),
      flagCountryCode: isoCountrySchema.nullable().optional(),
      vesselType: z.string().max(50).nullable().optional(),
      operator: z.string().max(200).nullable().optional(),
      outbreakId: z.number().int().positive().nullable().optional(),
      notes: z.string().nullable().optional(),
    })
    .refine((p) => Object.keys(p).length > 0, {
      message: "patch must contain at least one field",
    }),
});

export const adminVesselTrackingInputSchema = z.object({
  id: z.number().int().positive(),
});

// ---------------------------------------------------------------------------
// admin.users.*  (super-admin only)
// ---------------------------------------------------------------------------

export const adminRoleSchema = z.enum(["curator", "editor", "super_admin"]);
export type AdminRoleInput = z.infer<typeof adminRoleSchema>;

export const adminUsersListInputSchema = z.object({}).strict().optional();

export const adminUsersPromoteInputSchema = z.object({
  email: z.string().email(),
  role: adminRoleSchema.default("curator"),
  bio: z.string().max(2000).optional(),
});

export const adminUsersDemoteInputSchema = z.object({
  userId: z.string().min(1),
});

export const adminAuditLogInputSchema = z.object({
  outbreakId: z.number().int().positive().optional(),
  cursor: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).default(50),
});

export const adminAuditLogRowSchema = z.object({
  id: z.number().int(),
  outbreakId: z.number().int(),
  changedAt: z.date(),
  changeType: z.enum([
    "cases_updated",
    "status_changed",
    "risk_level_changed",
    "timeline_added",
    "advisory_added",
    "news_curated",
  ]),
  beforeJson: z.unknown(),
  afterJson: z.unknown(),
  triggeredBy: z.enum(["scraper", "admin", "system"]),
  triggerActorId: z.string().nullable(),
});

export const adminAuditLogOutputSchema = z.object({
  items: z.array(adminAuditLogRowSchema),
  nextCursor: z.number().int().min(0).nullable(),
});
export type AdminAuditLogOutput = z.infer<typeof adminAuditLogOutputSchema>;
